import { animate, state, style, transition, trigger } from '@angular/animations'
import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter, ViewChildren, QueryList, ChangeDetectorRef, DoCheck, signal } from '@angular/core'
import { disabled, form, required } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'
import { DomSanitizer } from '@angular/platform-browser'
import { filter, takeWhile } from 'rxjs'

import { TilinpaatosLiitetiedot, TilinpaatosLiitetiedotKentat, TilinpaatosLiitetiedotOsio } from '../../_jaettu-lemonator/model/kirjanpito'
import { InfoDialog, InfoDialogData } from '../../_jaettu-angular/_components/info.dialog'
import { Sidonta } from './sidonta'

export type DoneCallbackHandlerFunction = (outcome: 'no-save-needed' | 'error' | 'success', error?: Error) => void
export interface TallennaArvoEvent {
  field: TilinpaatosLiitetiedotKentat
  type: 'text' | 'number' | 'checkbox'
  doneCallback?: DoneCallbackHandlerFunction
}

interface LiitetietoLomake {
  valittu: boolean | null
  teksti: string
  numero: number | null
}

@Component({
  selector: '[app-kirjanpito-liitetiedot-checkbox]',
  templateUrl: './liitetiedot-checkbox.component.html',
  styleUrls: ['./liitetiedot-checkbox.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    // the fade-in/fade-out animation.
    trigger('slowInQuickOut', [
      // the "in" style determines the "resting" state of the element when it is visible.
      state('in', style({ opacity: 1 })),
      // fade in when created. this could also be written as transition('void => *')
      transition(':enter', [style({ opacity: 0 }), animate('600ms 3s')]),
      // fade out when destroyed. this could also be written as transition('* => void')
      transition(':leave', animate(100, style({ opacity: 0 }))),
    ]),
    // the fade-in/fade-out animation.
    trigger('quickInSlowOut', [
      // the "in" style determines the "resting" state of the element when it is visible.
      state('in', style({ opacity: 1 })),
      // fade in when created. this could also be written as transition('void => *')
      transition(':enter', [style({ opacity: 0 }), animate(100)]),
      // fade out when destroyed. this could also be written as transition('* => void')
      transition(':leave', animate(600, style({ opacity: 0 }))),
    ]),
  ],
  standalone: false,
})
export class KirjanpitoTilinpaatosLiitetiedotCheckboxComponent implements OnInit, DoCheck {
  @Output() tallennaArvo: EventEmitter<TallennaArvoEvent> = new EventEmitter<TallennaArvoEvent>()

  @ViewChildren(MatInput) kirjoitusKomponentit: QueryList<MatInput>

  @Output() checkboxChanged: EventEmitter<void> = new EventEmitter()

  @Input() parent: TilinpaatosLiitetiedotOsio
  @Input() order: number
  private _example: string
  rawExample: string
  get example(): string {
    return this._example
  }
  @Input() set example(value: string) {
    this.rawExample = value
    this._example = this._sanitizer.bypassSecurityTrustHtml(value) as string
  }
  @Input() info: string
  @Input() field: TilinpaatosLiitetiedotKentat
  @Input() unsaved: TilinpaatosLiitetiedot
  @Input() liitetiedot: TilinpaatosLiitetiedot
  @Input() type: 'text' | 'number' = 'text'
  private readonly _checkboxDisabled = signal(false)
  @Input() set checkboxDisabled(checkboxDisabled: boolean) {
    this._checkboxDisabled.set(checkboxDisabled)
  }
  get checkboxDisabled(): boolean {
    return this._checkboxDisabled()
  }
  private readonly _formDisabled = signal(false)
  @Input() set formDisabled(formDisabled: boolean) {
    this._formDisabled.set(formDisabled)
  }
  get formDisabled(): boolean {
    return this._formDisabled()
  }

  lomake = form(signal<LiitetietoLomake>({ valittu: null, teksti: '', numero: null }), polku => {
    disabled(polku.valittu, () => this._checkboxDisabled() || this._formDisabled())
    disabled(polku.teksti, () => this._formDisabled())
    disabled(polku.numero, () => this._formDisabled())
    required(polku.numero)
  })
  private readonly _valinnanSidonta = new Sidonta(this.lomake.valittu)
  private readonly _tekstinSidonta = new Sidonta(this.lomake.teksti)
  private readonly _numeronSidonta = new Sidonta(this.lomake.numero)

  namename = 'feoklpsfvmuysdfg' + Math.random()
  saving: boolean = false
  saveSuccess: boolean = false
  saveFailure: boolean = false

  constructor(
    private _dialog: MatDialog,
    private _changeDetectorRef: ChangeDetectorRef,
    private _sanitizer: DomSanitizer,
  ) {}

  ngOnInit() {
    // console.log('Init', this.field)
  }

  // Liitetietonäkymä päivittää liitetietoja paikallaan, joten sidotut arvot luetaan jokaisella tarkistuksella
  ngDoCheck() {
    this._valinnanSidonta.sido(this.liitetiedot.valitut[this.field])
    this._tekstinSidonta.sido(this.liitetiedot.tekstit[this.field], this.liitetiedot.tekstit[this.field] ?? '')
    this._numeronSidonta.sido(this.liitetiedot.numerot[this.field])
  }

  avaaInfoTeksti(teksti: string) {
    const data: InfoDialogData = {
      text: teksti,
      header: '',
    }
    this._dialog.open<InfoDialog, InfoDialogData>(InfoDialog, { data: data })
  }

  checkboxChangedEventHandler(event: boolean) {
    this.unsaved.valitut[this.field] = !!event
    this.checkboxChanged.next()
    this.tallennaMuuttunutArvoWithType('checkbox')
    if (event) {
      this.kirjoitusKomponentit.changes
        .pipe(
          takeWhile(() => this.kirjoitusKomponentit.length < 1, true),
          filter(() => this.kirjoitusKomponentit.length > 0),
        )
        .subscribe(() => {
          this.kirjoitusKomponentit.first.focus()
        })
    }
  }

  textChangedEventHandler(event: string) {
    this.unsaved.tekstit[this.field] = event
  }

  numberChangedEventHandler(event: number) {
    this.unsaved.numerot[this.field] = event
    this.checkboxChanged.next()
  }

  tallennaMuuttunutArvo() {
    this.tallennaMuuttunutArvoWithType(this.type)
  }

  tallennaMuuttunutArvoWithType(type: 'text' | 'number' | 'checkbox') {
    this.saveFailure = false
    this.saveSuccess = false
    this.saving = true
    const tallennaEvent: TallennaArvoEvent = {
      field: this.field,
      type: type,
      doneCallback: outcome => {
        this.saving = false
        if (outcome === 'error') {
          this.saveFailure = true
          this.saveSuccess = false
        } else if (outcome === 'success') {
          this.saveFailure = false
          this.saveSuccess = true
        } else {
          this.saveFailure = false
          this.saveSuccess = false
        }
        this._changeDetectorRef.markForCheck()
        setTimeout(() => {
          this.saveSuccess = false
          this._changeDetectorRef.markForCheck()
          setTimeout(() => {
            this._changeDetectorRef.markForCheck()
            this._changeDetectorRef.detectChanges()
          }, 250)
        }, 1000)
      },
    }
    this.tallennaArvo.next(tallennaEvent)
  }
}
